import { Alert, Button, Group, List, Stack, Text } from "@mantine/core";
import { IconEye, IconPlus } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { generatePath, Link } from "react-router";
import { AnchorLink } from "../../../components/AnchorLink/AnchorLink";
import { Extendable } from "../../../components/Extendable/Extendable";
import { LoadOrError } from "../../../components/LoadOrError/LoadOrError";
import { OptiSupportHelpLink } from "../../../components/OptiSupportHelpLink/OptiSupportHelpLink";
import { useShouldShowStaffView } from "../../../components/StaffViewToggle/useShouldShowStaffView";
import { useFeatureFlags } from "../../../hooks/useFeatureFlags";
import { useProject } from "../../../hooks/useProject";
import { useVariantDetail } from "../../../hooks/useVariantDetail";
import { IconButton } from "../../../mantine/components/IconButton/IconButton";
import { AlertPresets } from "../../../mantine/mantinePresets";
import { useToast } from "../../../mantine/notifications/useToast";
import { ROUTES } from "../../../routes";
import { Paragraph6EEGCreditResponseState } from "../../../types/api.types";
import { OptiSupportEndpoints } from "../../../urls";
import { backendDateOrDateTimeToLuxonDateTimeOrNull } from "../../../utils/dates/backendDateOrDateTimeToLuxonDateTimeOrNull";
import { DATE_DISPLAY_FORMAT } from "../../../utils/dates/dates.constants";
import { SiteName } from "../../../utils/enums";
import { useParagraph6Contracts } from "../hooks/useParagraph6Contracts";
import { useParagraph6CreatableCredits } from "../hooks/useParagraph6CreatableCredits";
import { useParagraph6CreditErrors } from "../hooks/useParagraph6CreditErrors";
import { useParagraph6Credits } from "../hooks/useParagraph6Credits";
import { FinancialDocumentType } from "../Paragraph6.types";
import { Paragraph6AvailableCreditsModal } from "./Paragraph6AvailableCreditsModal/Paragraph6AvailableCreditsModal";
import { Paragraph6CreditErrorMessage } from "./Paragraph6CreditErrorMessage/Paragraph6CreditErrorMessage";
import { Paragraph6CreditLoadErrorMessage } from "./Paragraph6CreditErrorMessage/Paragraph6CreditLoadErrorMessage";
import "./Paragraph6CreditsPage.scss";
import { Paragraph6CreditsTable } from "./Paragraph6CreditsTable/Paragraph6CreditsTable";
import { UsePaymentInformationToggleSwitch } from "./UsePaymentInformationToggleSwitch/UsePaymentInformationToggleSwitch";

declare const SITE_NAME: SiteName;

const REFUND_LINK_TEXT = "Rückerstattung finanzielle Beteiligung nach § 6 EEG";

interface Paragraph6CreditsPageProps {
  variantId: number;
  projectId: string;
}

function Paragraph6CreditsPage({
  variantId,
  projectId
}: Paragraph6CreditsPageProps) {
  const projectTodosRoute = generatePath(ROUTES.todos.entry, {
    projectId
  });
  const { showToast } = useToast();

  const {
    featureFlags: { blockRueckerstattungP6Eeg },
    isLoading: featureFlagsLoading
  } = useFeatureFlags();

  const {
    creatableCredits,
    error: paragraph6CreatableCreditsError,
    createParagraph6Credits,
    fetchParagraph6DraftCreditStatus
  } = useParagraph6CreatableCredits(variantId);

  const {
    isLoading: paymentInformationIsLoading,
    error: paymentInformationError,
    variantDetail
  } = useVariantDetail(variantId);

  const {
    credits = [],
    isLoading: paragraph6CreditsLoading,
    error: paragraph6CreditsError
  } = useParagraph6Credits(variantId);

  const { project, isLoading: projectLoading } = useProject(projectId, {
    refetchInterval: false,
    refetchOnWindowFocus: false
  });

  const isProjectLoading = projectLoading || featureFlagsLoading;

  const isBlockRueckerstattungP6EegActive =
    isProjectLoading ||
    blockRueckerstattungP6Eeg ||
    project?.manualBlockRueckerstattungP6EegActive;

  const isCreatableCreditsEmpty =
    !creatableCredits || creatableCredits.length === 0;

  const usePaymentInformation =
    variantDetail?.paragraph_6_payment_information_instead_of_credit;

  const {
    creditErrors,
    isLoading: creditErrorsLoading,
    error: creditErrorsError
  } = useParagraph6CreditErrors(variantId);
  const { shouldShowStaffView } = useShouldShowStaffView();

  const {
    paragraph6Contracts,
    isLoading: paragraph6ContractsLoading,
    error: paragraph6ContractsError
  } = useParagraph6Contracts(variantId);

  const [availableCreditsModalOpen, setAvailableCreditsModalOpen] =
    useState(false);
  const [isPreviewModal, setIsPreviewModal] = useState(false);

  useEffect(() => {
    let SupportLink = <>unseren Support</>;
    if (SITE_NAME === SiteName.Optinode) {
      SupportLink = (
        <AnchorLink href="mailto:support@node.energy" target="_self">
          support@node.energy
        </AnchorLink>
      );
    }
    if (SITE_NAME === SiteName.Smarendo) {
      SupportLink = (
        <AnchorLink href="mailto:smarendo@node.energy" target="_self">
          smarendo@node.energy
        </AnchorLink>
      );
    }

    if (isBlockRueckerstattungP6EegActive === true) {
      showToast({
        type: "warning",
        title: "Erstellung einer Gutschrift nicht möglich",
        message: (
          <>
            In dieser Liegenschaft ist die Erstellung einer Gutschrift aktuell
            nicht möglich. Bitte wenden Sie sich an {SupportLink}, damit wir das
            gemeinsam mit Ihnen lösen können.
          </>
        ),
        id: "paragraph-6-credit-generation-blocked-alert",
        autoClose: false,
        withCloseButton: true
      });
    }
  }, [isBlockRueckerstattungP6EegActive, showToast]);

  function openGenerateCreditsModal() {
    setIsPreviewModal(false);
    setAvailableCreditsModalOpen(true);
  }

  function openPreviewCreditsModal() {
    setIsPreviewModal(true);
    setAvailableCreditsModalOpen(true);
  }

  // Imprecise fiktive Strommengen do not prevent credit creation, so the alert is
  // also needed when the only affected credits are the creatable ones.
  const hasCreditErrorsOrHints =
    (creditErrors && creditErrors.length > 0) ||
    (creatableCredits ?? []).some(
      (creatableCredit) => creatableCredit.fiktive_strommengen_are_imprecise
    );

  const availableRefunds = credits.filter(
    (credit) => credit.state === Paragraph6EEGCreditResponseState.paid
  );
  const availableRefundsTodoUrls = Array.from(
    new Set(
      availableRefunds
        .filter((credit) => !!credit.todo_id)
        .map((credit) => credit.todo_id)
    )
  ) as Array<number>;
  const availableRefundsTodos = availableRefundsTodoUrls.map((todoId) => {
    const credit = credits.find((credit) => credit.todo_id === todoId);
    return {
      todoId,
      supplier: credit?.supplier ?? "Unbekannt",
      due_date:
        backendDateOrDateTimeToLuxonDateTimeOrNull(
          credit?.todo_due_date
        )?.toFormat(DATE_DISPLAY_FORMAT) ?? "Unbekannt",
      gridOperator: credit?.grid_operator ?? "Unbekannt"
    };
  });

  return (
    <LoadOrError
      error={
        paragraph6ContractsError ||
        paragraph6CreatableCreditsError ||
        paymentInformationError ||
        paragraph6CreditsError
      }
      loading={
        paragraph6ContractsLoading ||
        paymentInformationIsLoading ||
        creditErrorsLoading ||
        paragraph6CreditsLoading
      }
      loadingMessage="Vertragsinformationen werden geladen ..."
    >
      <Stack className="Paragraph6CreditsPage" gap={0}>
        <Paragraph6AvailableCreditsModal
          contentType={
            usePaymentInformation
              ? FinancialDocumentType.PaymentInformation
              : FinancialDocumentType.Credit
          }
          contracts={paragraph6Contracts}
          creatableCredits={creatableCredits}
          createParagraph6Credits={createParagraph6Credits}
          fetchDraftCreditStatus={fetchParagraph6DraftCreditStatus}
          isOpen={availableCreditsModalOpen}
          previewMode={isPreviewModal}
          variantId={variantId}
          onClose={() => setAvailableCreditsModalOpen(false)}
        />
        <Stack gap="lg" mb="md">
          {usePaymentInformation ? (
            <OptiSupportHelpLink
              optiSupportEndpoint={
                OptiSupportEndpoints.ZahlungsinformationenZurKommunalenBeteiligungNach6Eeg
              }
              text="Wie lassen sich mit opti.node Zahlungsinformationen erstellen?"
            />
          ) : (
            <OptiSupportHelpLink
              optiSupportEndpoint={
                OptiSupportEndpoints.KommunaleBeteiligungNachParagraph6Eeg
              }
              text="Wie lassen sich mit opti.node Gutschriften und Stornos erstellen?"
            />
          )}
          {shouldShowStaffView && (
            <Group justify="flex-end">
              <UsePaymentInformationToggleSwitch
                usePaymentInformation={!!usePaymentInformation}
                variantId={variantId}
              />
            </Group>
          )}
        </Stack>
        {availableRefundsTodos.length > 0 && (
          <Alert
            color={AlertPresets.info.color}
            icon={AlertPresets.info.icon}
            mb="md"
          >
            Sobald Sie eine Gutschrift auf den Status <b>Bezahlt</b> gesetzt
            haben, ist die Rückerstattungsoption im Bereich{" "}
            <Link to={projectTodosRoute}>Aufgaben</Link> verfügbar. Sind alle
            Gutschriften eines Jahres auf <b>Bezahlt</b> gesetzt, können Sie den
            Rückerstattungsprozess starten.
            <br />
            Falls Sie Änderungen an einer Gutschrift vorgenommen haben und die
            Meldeunterlagen bereits erstellt wurden, sollten Sie die
            Meldeunterlagen erneut erstellen, um die geänderte Gutschrift
            korrekt zu berücksichtigen.
            <List mt="md">
              {availableRefundsTodos.slice(0, 5).map((todo) => (
                <List.Item key={todo.todoId}>
                  <Link to={`../../aufgaben/${todo.todoId}`}>
                    {REFUND_LINK_TEXT}
                  </Link>{" "}
                  ({todo.supplier}, {todo.gridOperator}, {todo.due_date})
                </List.Item>
              ))}
              {availableRefundsTodos.length > 5 && (
                <Extendable>
                  {availableRefundsTodos.slice(5).map((todo) => (
                    <List.Item key={todo.todoId}>
                      <Link to={`../../aufgaben/${todo.todoId}`}>
                        {REFUND_LINK_TEXT}
                      </Link>{" "}
                      ({todo.supplier}, {todo.gridOperator}, {todo.due_date})
                    </List.Item>
                  ))}
                </Extendable>
              )}
            </List>
          </Alert>
        )}
        <div className="credits-controls">
          <Stack gap={0} style={{ alignSelf: "flex-end" }}>
            {isBlockRueckerstattungP6EegActive && (
              <Text c="error">Diese Funktion ist zur Zeit deaktiviert.</Text>
            )}
            <div className="credits-buttons">
              {usePaymentInformation ? (
                <Button
                  color="primary"
                  disabled={
                    isBlockRueckerstattungP6EegActive || isCreatableCreditsEmpty
                  }
                  onClick={openGenerateCreditsModal}
                >
                  Verfügbare Zahlungsinformationen
                </Button>
              ) : (
                <IconButton
                  color="primary"
                  disabled={
                    isBlockRueckerstattungP6EegActive || isCreatableCreditsEmpty
                  }
                  icon={IconPlus}
                  onClick={openGenerateCreditsModal}
                >
                  Gutschriften erstellen
                </IconButton>
              )}
              <IconButton
                color="primary"
                disabled={isCreatableCreditsEmpty}
                icon={IconEye}
                variant="outline"
                onClick={openPreviewCreditsModal}
              >
                Vorschau
              </IconButton>
            </div>
          </Stack>
          {creditErrorsError && <Paragraph6CreditLoadErrorMessage />}
          {!creditErrorsError && hasCreditErrorsOrHints && (
            <Paragraph6CreditErrorMessage
              contracts={paragraph6Contracts}
              creatableCredits={creatableCredits}
              paragraph6CreditErrors={creditErrors ?? []}
            />
          )}
        </div>
        <Paragraph6CreditsTable
          isPaymentInformationView={usePaymentInformation}
          variantId={variantId}
        />
      </Stack>
    </LoadOrError>
  );
}

export { Paragraph6CreditsPage, Paragraph6CreditsPageProps };
